import { z } from 'zod';

// ---- Fiscal periods ----
export const FiscalPeriodSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().regex(/^\d{4}-\d{2}$/),
  periodStart: z.string(),
  periodEnd: z.string(),
  status: z.enum(['open', 'closed']),
  closedAt: z.string().datetime().nullable(),
  /** Who closed it — resolved to a display name for the periods table. */
  closedByName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type FiscalPeriod = z.infer<typeof FiscalPeriodSchema>;

/** One close/reopen event on the periods page's activity feed. */
export interface FiscalPeriodActivity {
  id: string;
  action: 'close' | 'reopen';
  periodCode: string | null;
  actorName: string | null;
  at: string;
}

// ---- Journal entries ----
export const JournalLineInputSchema = z.object({
  accountId: z.string().uuid(),
  description: z.string().max(500).optional(),
  /** Transaction-currency debit. Either debit OR credit must be > 0, not both. */
  debit: z.string().regex(/^\d+(\.\d{1,4})?$/).default('0'),
  credit: z.string().regex(/^\d+(\.\d{1,4})?$/).default('0'),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  /** Optional party reference (customer/vendor) for AR/AP lines. */
  partyId: z.string().uuid().optional(),
});
export type JournalLineInput = z.infer<typeof JournalLineInputSchema>;

export const JournalEntryInputSchema = z.object({
  entryDate: z.string(),
  description: z.string().max(2000).optional(),
  sourceType: z.string().max(64).optional(),
  sourceId: z.string().uuid().optional(),
  lines: z.array(JournalLineInputSchema).min(2),
});
export type JournalEntryInput = z.infer<typeof JournalEntryInputSchema>;

export const JournalLineSchema = z.object({
  id: z.string().uuid(),
  entryId: z.string().uuid(),
  lineNo: z.number().int().nonnegative(),
  accountId: z.string().uuid(),
  accountCode: z.string(),
  accountName: z.string(),
  description: z.string().nullable(),
  transactionCurrencyCode: z.string().length(3),
  debit: z.string(),
  credit: z.string(),
  baseCurrencyCode: z.string().length(3),
  baseDebit: z.string(),
  baseCredit: z.string(),
  exchangeRate: z.string(),
  partyId: z.string().uuid().nullable(),
});
export type JournalLine = z.infer<typeof JournalLineSchema>;

export const JournalEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  entryNumber: z.string(),
  entryDate: z.string(),
  periodId: z.string().uuid(),
  description: z.string().nullable(),
  sourceType: z.string().nullable(),
  sourceId: z.string().uuid().nullable(),
  reversedById: z.string().uuid().nullable(),
  reversesId: z.string().uuid().nullable(),
  postedAt: z.string().datetime(),
  postedByUserId: z.string().uuid().nullable(),
  lines: z.array(JournalLineSchema),
  createdAt: z.string().datetime(),
});
export type JournalEntry = z.infer<typeof JournalEntrySchema>;

// ---- Account ledger (a single account's posting history) ----
/** A journal line enriched with its entry's number + date, for an account ledger. */
export const AccountLedgerRowSchema = JournalLineSchema.extend({
  entryNumber: z.string(),
  entryDate: z.string(),
});
export type AccountLedgerRow = z.infer<typeof AccountLedgerRowSchema>;

export const AccountLedgerSchema = z.object({
  items: z.array(AccountLedgerRowSchema),
  total: z.number().int().nonnegative(),
});
export type AccountLedger = z.infer<typeof AccountLedgerSchema>;

/**
 * One account's ledger kept per TRANSACTION currency (GET
 * /general-ledger/accounts/:id/ledger): balance brought forward at `from`, the
 * window's postings (newest first, the latest `limit`) each with its running
 * balance in its own currency and in base, and the closing balances. Signs
 * follow the account's normal side.
 */
export interface AccountCurrencyLedger {
  accountId: string;
  debitNormal: boolean;
  from: string | null;
  to: string | null;
  currencies: Array<{
    currencyCode: string;
    opening: string;
    debit: string;
    credit: string;
    closing: string;
    /** This currency's share of the base balance (each posting at its own rate). */
    baseClosing: string;
  }>;
  baseOpening: string;
  baseClosing: string;
  /** Postings in the window (lines may hold fewer — the latest `limit`). */
  total: number;
  lines: Array<{
    entryId: string;
    entryNumber: string;
    entryDate: string;
    lineNo: number;
    description: string | null;
    sourceType: string | null;
    currencyCode: string;
    debit: string;
    credit: string;
    baseDebit: string;
    baseCredit: string;
    exchangeRate: string;
    /** Running balance in `currencyCode` after this posting. */
    balance: string;
    /** Running base balance (all currencies) after this posting. */
    baseBalance: string;
  }>;
}

// ---- Reports ----
export const TrialBalanceRowSchema = z.object({
  accountId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  accountCode: z.string(),
  accountName: z.string(),
  accountType: z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']),
  baseDebit: z.string(),
  baseCredit: z.string(),
  baseBalance: z.string(),
  /**
   * The same balance per TRANSACTION currency — what actually moved through the
   * account in each currency (a cash box holds dollars AND dinars; its base
   * balance alone hides how many dollars are in it). `baseBalance` is that
   * currency's share converted at each posting's own rate; the shares add up to
   * the row's baseBalance. Sign follows the account's normal side, like
   * baseBalance. Empty for an account with no postings.
   */
  byCurrency: z.array(z.object({
    currencyCode: z.string().length(3),
    debit: z.string(),
    credit: z.string(),
    balance: z.string(),
    baseBalance: z.string(),
  })).optional(),
});
export type TrialBalanceRow = z.infer<typeof TrialBalanceRowSchema>;
